import { Option } from '../options';
import { yearDiff } from '../util/time';
import { SurchargeAmountType, SurchargeType } from './model';
import { RaffleWithUserDetails } from './raffle';

export interface GenericOptionInput extends Option {
  required: boolean;
  mutable: boolean;
  productId?: number;
}
export interface ProductOptionInput extends GenericOptionInput {
  productOptionId: number;
}

export enum ProductVisibility {
  PUBLIC = 'public',
  UNLISTED = 'unlisted',
  HIDDEN = 'hidden',
}

export interface ProductReference {
  id: number;
  name: string;
  displayName: string | null;
}

export type ProductImageType = 'badgeArt' | 'hidden' | 'image' | 'primary';

export type ProductStockStatus = 'infinite' | 'inStock' | 'soldOut';

export interface ProductModel {
  id: number;
  conventionId: number;
  name: string;
  displayName: string | null;
  description?: string;
  availableFrom?: Date;
  availableTo?: Date;
  deletedAt?: Date;
  digitalGrantRestricted: boolean;
  maxGrants?: number;

  stockAvailable?: number;
  unusedStock?: number | null;
  stockMaximumQuantity?: number | null;
  stockStatus: ProductStockStatus;

  maxAge?: number;
  minAge?: number;
  visibility: ProductVisibility;
  price: number;
  isTicketed: boolean;

  roleRestriction: number[] | null;
  roleBlacklist: number[] | null;

  options: ProductOptionInput[];
  canAddMoreOptions: boolean;

  upgrades: ProductUpgradeModel[];

  discounts: ProductDiscount[];

  category: ProductCategoryModel;

  images: ProductImage[];
  canAddMoreImages: boolean;

  addons: ProductAddonModel[];

  requiredGrants: {
    productId: number;
    name: string;
    displayName: string | null;
  }[];

  surcharges: SurchargeModel[];

  raffle?: Pick<
    RaffleWithUserDetails,
    'enteredSubtext' | 'enteredTitle' | 'id' | 'overflowEntries' | 'roleOverride' | 'status'
  >;

  grantExpiresOn?: Date;
}

export interface ProductImage {
  id: string;
  type: ProductImageType;
}

export interface ProductCategoryModel {
  id: number;
  name: string;
  isStore: boolean;
  isDigital: boolean;
  isRegistration: boolean;
  isVendor: boolean;
}

export interface ProductUpgradeModel {
  id: number;
  name: string;
  displayName: string | null;
}

export interface ProductAddonModel {
  isFree: boolean;
  id: number;
  name: string;
  description: string | null;
  price: number;
  visibility: ProductVisibility;
  displayName: string | null;
  imageId?: string;
  options: ProductOptionInput[];
  discounts: ProductDiscount[];
  stockStatus: ProductStockStatus;
  stockMaximumQuantity?: number | null;
  stockAvailable?: number | null;
}

export interface SurchargeModel {
  id: number;
  internal: boolean;
  name: string;
  deletedAt?: Date;
  amountType: SurchargeAmountType;
  amount: number;
  type: SurchargeType;
}

export interface ProductDiscount {
  availableFrom: Date;
  availableTo: Date;
  deletedAt?: Date;
  id: number;
  name: string | null;
  price: number;
  roleId?: number;
}

export interface CreateProductDiscountPayload extends UpdateProductDiscountPayload {
  price: number;
  roleIds: number[];
}

export interface UpdateProductDiscountPayload {
  availableFrom: Date;
  availableTo: Date;
  name: string | null;
}

export interface ProductEligibility {
  canPurchase: boolean;
  alreadyHasTicket?: boolean;
  requireDigitalGrant?: boolean;
  grantLeft?: number;
  requiredProduct?: {
    name: string;
    displayName: string | null;
  };
}

export function inAgeRange(product: ProductModel, bornAt: Date | null, startAt: Date): boolean {
  if (product.minAge || product.maxAge) {
    if (!bornAt) {
      return false;
    }

    const age = yearDiff(bornAt, startAt);

    if (product.minAge && age < product.minAge) {
      return false;
    }

    if (product.maxAge && age > product.maxAge) {
      return false;
    }
  }

  return true;
}

export const noImage = 'https://cdn.concat.app/static/no-image.jpg';
